import { closingStatus } from '@libs/enums';
import { useLocalizedContents, useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchCurrentProcessingMonth } from '../repositories';
import { calculateEndingYearMonth } from '../services';

interface ProcessingMonthContextProps {
  processingMonth: Date;
  beginningYearMonth: Date;
  endingYearMonth: Date;
  carryOverState: string;
  processingMonthEom: Date;
  refetch: () => Promise<void>;
}

const ProcessingMonthContext = createContext<ProcessingMonthContextProps>({
  processingMonth: new Date(1900, 0, 1),
  beginningYearMonth: new Date(1900, 0, 1),
  endingYearMonth: new Date(1900, 0, 1),
  carryOverState: '',
  processingMonthEom: new Date(1900, 0, 1),
  refetch: async () => {},
});

function ProcessingMonthProvider({ children }: { children: ReactNode }) {
  const [processingMonth, setProcessingMonth] = useState<Date>(new Date(1900, 0, 1));
  const [beginningYearMonth, setBeginningYearMonth] = useState<Date>(new Date(1900, 0, 1));
  const [endingYearMonth, setEndingYearMonth] = useState<Date>(new Date(1900, 0, 1));
  const [carryOverState, setCarryOverState] = useState<string>('');
  const [processingMonthEom, setProcessingMonthEom] = useState<Date>(new Date(1900, 0, 1));
  const { locale } = useLocalizedContents();
  const { tryRequest } = useTry();

  const refetch = useCallback(
    async () =>
      await tryRequest(async () => {
        const basicInfoData = await fetchCurrentProcessingMonth();
        setProcessingMonth(basicInfoData.processingMonth);
        setBeginningYearMonth(basicInfoData.beginningYearMonth);
        const newEndingYearMonth = calculateEndingYearMonth(basicInfoData.beginningYearMonth);
        setEndingYearMonth(newEndingYearMonth);
        setCarryOverState(closingStatus.toDisplay(basicInfoData.carryOverState, locale));
        setProcessingMonthEom(basicInfoData.processingMonthEom);
      }),
    [],
  );

  useEffect(() => {
    void refetch();
  }, []);

  return (
    <ProcessingMonthContext.Provider
      value={{
        processingMonth,
        beginningYearMonth,
        endingYearMonth,
        carryOverState,
        processingMonthEom,
        refetch,
      }}
    >
      {children}
    </ProcessingMonthContext.Provider>
  );
}

function useProcessingMonth() {
  return useContext(ProcessingMonthContext);
}

export { ProcessingMonthProvider, useProcessingMonth };
